import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Cog6ToothIcon,
  EnvelopeIcon,
  BoltIcon,
  HandRaisedIcon,
  SparklesIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';

interface EmailAutomationSetting {
  applicationStatus: string;
  emailMode: 'AUTOMATED' | 'MANUAL';
}

const STATUS_LABELS: Record<string, { label: string; description: string }> = {
  REVIEWED: {
    label: 'Reviewed',
    description: 'When an application is marked as reviewed by a recruiter',
  },
  SHORTLISTED: {
    label: 'Shortlisted',
    description: 'When a candidate is shortlisted for the next round',
  },
  OFFERED: {
    label: 'Offered',
    description: 'When a job offer is extended to a candidate',
  },
  REJECTED: {
    label: 'Rejected',
    description: 'When an application is rejected',
  },
};

const AdminSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<EmailAutomationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [aiScoring, setAiScoring] = useState<{ envAllows: boolean; adminEnabled: boolean; effective: boolean } | null>(null);
  const [aiSaving, setAiSaving] = useState(false);
  const [statusVisibility, setStatusVisibility] = useState<{ autoPublish: boolean } | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'email' | 'ai' | 'status'>('email');

  useEffect(() => {
    fetchSettings();
    fetchAiScoring();
    fetchStatusVisibility();
  }, []);

  const fetchStatusVisibility = async () => {
    try {
      const response = await axiosInstance.get('/admin/status-publish-setting');
      setStatusVisibility(response.data);
    } catch (error) {
      console.error('Error fetching status visibility setting:', error);
    }
  };

  const handleStatusVisibilityToggle = async () => {
    if (!statusVisibility) return;
    setStatusSaving(true);
    try {
      const response = await axiosInstance.put('/admin/status-publish-setting', {
        autoPublish: !statusVisibility.autoPublish,
      });
      setStatusVisibility(response.data);
      toast.success(
        response.data.autoPublish
          ? 'Status changes are now shown to candidates automatically'
          : 'Status changes are now hidden until published'
      );
    } catch (error) {
      console.error('Error updating status visibility setting:', error);
      toast.error('Failed to update status visibility setting');
    } finally {
      setStatusSaving(false);
    }
  };

  const fetchAiScoring = async () => {
    try {
      const response = await axiosInstance.get('/admin/ai-scoring-setting');
      setAiScoring(response.data);
    } catch (error) {
      console.error('Error fetching AI scoring setting:', error);
    }
  };

  const handleAiToggle = async () => {
    if (!aiScoring) return;
    setAiSaving(true);
    try {
      const response = await axiosInstance.put('/admin/ai-scoring-setting', {
        enabled: !aiScoring.adminEnabled,
      });
      setAiScoring(response.data);
      toast.success(`AI candidate scoring ${response.data.effective ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating AI scoring setting:', error);
      toast.error('Failed to update AI scoring setting');
    } finally {
      setAiSaving(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/email-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (status: string, currentMode: string) => {
    const newMode = currentMode === 'AUTOMATED' ? 'MANUAL' : 'AUTOMATED';
    setSaving(status);
    try {
      const response = await axiosInstance.put(`/admin/email-settings/${status}`, {
        emailMode: newMode,
      });
      setSettings((prev) =>
        prev.map((s) =>
          s.applicationStatus === status ? { ...s, emailMode: response.data.emailMode } : s
        )
      );
      toast.success(
        `${STATUS_LABELS[status]?.label || status} emails set to ${newMode.toLowerCase()}`
      );
    } catch (error) {
      console.error('Error updating email setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <Cog6ToothIcon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure system-wide settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6" aria-label="Settings tabs">
          <button
            onClick={() => setActiveTab('email')}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Email Automation
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'ai'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            AI Candidate Scoring
          </button>
          <button
            onClick={() => setActiveTab('status')}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'status'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Status Visibility
          </button>
        </nav>
      </div>

      {/* Email Automation Settings Section */}
      {activeTab === 'email' && (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <EnvelopeIcon className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Email Automation
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Choose whether candidate emails are sent automatically on status change or manually
                by an admin
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {statusVisibility?.autoPublish === false && (
            <div className="mb-5 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <span className="font-medium">Status Visibility is set to Manual.</span> Automated
                emails below are sent when you publish a status, not when you set it — the email
                names the status, so sending it earlier would contradict the candidate’s dashboard.
                Manual emails are unaffected, and sending one publishes that status.
              </p>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {settings.map((setting) => {
                const info = STATUS_LABELS[setting.applicationStatus];
                if (!info) return null;
                const isAutomated = setting.emailMode === 'AUTOMATED';
                const isSaving = saving === setting.applicationStatus;

                return (
                  <div
                    key={setting.applicationStatus}
                    className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {info.label}
                        </h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            isAutomated
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}
                        >
                          {isAutomated ? (
                            <>
                              <BoltIcon className="h-3 w-3 mr-1" />
                              Automated
                            </>
                          ) : (
                            <>
                              <HandRaisedIcon className="h-3 w-3 mr-1" />
                              Manual
                            </>
                          )}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {info.description}
                      </p>
                      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                        {isAutomated
                          ? 'Email is sent automatically when this status is applied'
                          : 'Admin will be prompted to send an email manually after changing to this status'}
                      </p>
                    </div>

                    <div className="ml-4 flex-shrink-0">
                      <button
                        onClick={() =>
                          handleToggle(setting.applicationStatus, setting.emailMode)
                        }
                        disabled={isSaving}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                          isAutomated
                            ? 'bg-indigo-600 dark:bg-indigo-500'
                            : 'bg-gray-200 dark:bg-gray-600'
                        } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isAutomated ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}

              {settings.length === 0 && !loading && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                  No email settings found
                </p>
              )}
            </div>
          )}

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Automated:</strong> Emails are sent automatically when an application status
              changes. No admin action needed.
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">
              <strong>Manual:</strong> After changing the status, a modal will appear allowing the
              admin to review, customize, and send the email to the candidate.
            </p>
          </div>
        </div>
      </div>
      )}

      {/* AI Candidate Scoring Section */}
      {activeTab === 'ai' && (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <SparklesIcon className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                AI Candidate Scoring
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Controls whether résumés are analysed and scored by AI (EU AI Act). Turning this off
                pauses candidate profiling across the app.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {aiScoring === null ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Enable AI scoring
                    </h3>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        aiScoring.effective
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}
                    >
                      {aiScoring.effective ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {aiScoring.envAllows
                      ? 'When on, résumés are analysed and scored against the job. When off, no profiling occurs.'
                      : 'Scoring is disabled at the environment level (compliance hard-stop) and cannot be enabled here.'}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={handleAiToggle}
                    disabled={aiSaving || !aiScoring.envAllows}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      aiScoring.adminEnabled ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-gray-600'
                    } ${aiSaving || !aiScoring.envAllows ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        aiScoring.adminEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {!aiScoring.envAllows && (
                <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    AI scoring is switched off via the <code>AI_SCORING_ENABLED</code> environment
                    variable — the compliance master switch. An admin cannot re-enable scoring here
                    until it is turned back on at the environment level.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      )}

      {activeTab === 'status' && (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <EyeIcon className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Status Visibility
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Choose whether candidates see application status changes as they happen, or only
                once a hiring manager publishes them
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {statusVisibility === null ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Show status changes automatically
                    </h3>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        statusVisibility.autoPublish
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}
                    >
                      {statusVisibility.autoPublish ? (
                        <>
                          <BoltIcon className="h-3 w-3 mr-1" />
                          Automatic
                        </>
                      ) : (
                        <>
                          <HandRaisedIcon className="h-3 w-3 mr-1" />
                          Manual
                        </>
                      )}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {statusVisibility.autoPublish
                      ? 'Candidates see each status change as soon as it is made.'
                      : 'Candidates keep seeing “Applied” until a hiring manager publishes the status from the job’s applicant list.'}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={handleStatusVisibilityToggle}
                    disabled={statusSaving}
                    aria-label="Toggle automatic status visibility"
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      statusVisibility.autoPublish ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-gray-600'
                    } ${statusSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        statusVisibility.autoPublish ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {!statusVisibility.autoPublish && (
                <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    While this is manual, status emails are held back too — an email naming the new
                    status would tell the candidate exactly what is being withheld. They go out when
                    the status is published. Withdrawals and offer responses always stay visible,
                    since the candidate made them.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;
